import React, { useState } from 'react';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useAppServices } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AdminServicesSection({ appId, showToast }) {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();
  
  // Query hook
  const { data: services = [], isLoading: loading, refetch } = useAppServices(appId);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    serviceName: '',
    description: '',
    serviceUrl: '',
  });

  const loadServices = () => {
    refetch();
  };

  const resetForm = () => {
    setFormData({
      serviceName: '',
      description: '',
      serviceUrl: '',
    });
  };

  const handleAddService = async () => {
    if (!formData.serviceName.trim()) {
      showToast('Service name is required', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const response = await authFetch('/api/admin/services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          serviceName: formData.serviceName,
          description: formData.description,
          serviceUrl: formData.serviceUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add service');
      }

      showToast('Service added successfully', 'success');
      setShowAddModal(false);
      resetForm();
      loadServices();
    } catch (error) {
      captureError(error, {
        action: 'handleAddService',
        component: 'AdminServicesSection',
        extra: { appId, formData },
      });
      console.error('Error adding service:', error);
      showToast(error.message || 'Failed to add service', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditService = async () => {
    if (!formData.serviceName.trim()) {
      showToast('Service name is required', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const response = await authFetch('/api/admin/services', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingService.id,
          serviceName: formData.serviceName,
          description: formData.description,
          serviceUrl: formData.serviceUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update service');
      }

      showToast('Service updated successfully', 'success');
      setShowEditModal(false);
      setEditingService(null);
      resetForm();
      loadServices();
    } catch (error) {
      captureError(error, {
        action: 'handleEditService',
        component: 'AdminServicesSection',
        extra: { appId, serviceId: editingService?.id, formData },
      });
      console.error('Error updating service:', error);
      showToast(error.message || 'Failed to update service', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setDeletingId(serviceId);
      const response = await authFetch('/api/admin/services', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: serviceId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete service');
      }

      showToast('Service deleted', 'success');
      loadServices();
    } catch (error) {
      captureError(error, {
        action: 'handleDeleteService',
        component: 'AdminServicesSection',
        extra: { appId, serviceId },
      });
      console.error('Error deleting service:', error);
      showToast(error.message || 'Failed to delete service', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      serviceName: service.serviceName || '',
      description: service.description || '',
      serviceUrl: service.serviceUrl || '',
    });
    setShowEditModal(true);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>🔗</span>
            <span>External Services</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Track external services used by this app with direct links
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 text-sm font-medium flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Service
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading services...</div>
      ) : services.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="mb-2">No services added yet</p>
          <p className="text-xs">Add external services like Vercel, Sentry, Stripe, etc.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <div
              key={service.id}
              className="flex items-start justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900">{service.serviceName}</h4>
                  {service.serviceUrl && (
                    <a
                      href={service.serviceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zapt-purple hover:text-zapt-purple/80 transition-colors"
                      title="Open service"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
                {service.description && (
                  <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                )}
                {service.serviceUrl && (
                  <p className="text-xs text-gray-400 font-mono truncate">{service.serviceUrl}</p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => openEditModal(service)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                  title="Edit service"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteService(service.id)}
                  disabled={deletingId === service.id}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  title="Delete service"
                >
                  {deletingId === service.id ? (
                    <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add External Service</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.serviceName}
                  onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                  placeholder="e.g., Vercel, Sentry, Stripe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="How is this service used for this app?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service URL
                </label>
                <input
                  type="url"
                  value={formData.serviceUrl}
                  onChange={(e) => setFormData({ ...formData, serviceUrl: e.target.value })}
                  placeholder="https://vercel.com/team/project"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Direct link to this service for this app
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddService}
                disabled={submitting || !formData.serviceName.trim()}
                className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Adding...' : 'Add Service'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditModal && editingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Edit Service</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.serviceName}
                  onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                  placeholder="e.g., Vercel, Sentry, Stripe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="How is this service used for this app?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service URL
                </label>
                <input
                  type="url"
                  value={formData.serviceUrl}
                  onChange={(e) => setFormData({ ...formData, serviceUrl: e.target.value })}
                  placeholder="https://vercel.com/team/project"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Direct link to this service for this app
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingService(null);
                  resetForm();
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditService}
                disabled={submitting || !formData.serviceName.trim()}
                className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
